// ============================================
// VALIDADORES DE DOCUMENTOS DE IDENTIFICACIÓN
// Funciones puras para validar documentos de El Salvador
// ============================================

/**
 * Valida un DUI (Documento Único de Identidad) de El Salvador
 * Formato: ########-# (8 dígitos + dígito verificador)
 */
export const validateDUI = (dui) => {
  if (!dui) return false;
  if (!/^\d{8}-\d$/.test(dui)) return false;

  const [num, dv] = dui.split("-");
  let suma = 0;

  for (let i = 0; i < 8; i++) {
    suma += parseInt(num[i]) * (9 - i);
  }

  const verificador = (10 - (suma % 10)) % 10;

  return verificador === parseInt(dv);
};

/**
 * Valida un NIT (Número de Identificación Tributaria) de El Salvador
 * Formato: ####-######-###-# (4-6-3-1 = 14 dígitos)
 */
export const validateNIT = (nit) => {
  if (!nit) return false;
  // Validar formato con guiones
  if (!/^\d{4}-\d{6}-\d{3}-\d$/.test(nit)) return false;

  const clean = nit.replace(/-/g, "");
  
  // Validar que tenga exactamente 14 dígitos
  if (clean.length !== 14) return false;
  
  // Los primeros 4 dígitos son el código de registro (debe ser 0000-9999)
  const codigoRegistro = parseInt(clean.substring(0, 4));
  if (isNaN(codigoRegistro) || codigoRegistro < 0 || codigoRegistro > 9999) return false;
  
  // Posiciones 4-9: fecha de nacimiento (DDMMAA)
  const fecha = clean.substring(4, 10);
  const dia = parseInt(fecha.substring(0, 2));
  const mes = parseInt(fecha.substring(2, 4));
  const anio = parseInt(fecha.substring(4, 6));
  
  if (isNaN(dia) || dia < 1 || dia > 31) return false;
  if (isNaN(mes) || mes < 1 || mes > 12) return false;
  if (isNaN(anio) || anio < 0 || anio > 99) return false;
  
  return true;
};

/**
 * Valida un NRC (Número de Registro de Contribuyente)
 * Formato: 1-8 dígitos
 */
export const validateNRC = (nrc) => {
  if (!nrc) return false;
  return /^\d{1,8}$/.test(nrc);
};

/**
 * Valida un Carnet de Residente
 * Formato: Letra seguida de 6-7 dígitos (ej: A1234567)
 */
export const validateCarnetResidente = (cr) => {
  if (!cr) return false;
  const clean = cr.replace(/[\s-]/g, "").toUpperCase();
  return /^[A-Z]\d{6,7}$/.test(clean);
};

/**
 * Valida un Pasaporte
 * Formato: Letra seguida de 6-8 números (ej: A1234567)
 */
export const validatePasaporte = (pass) => {
  if (!pass) return false;
  const clean = pass.replace(/\s/g, "").toUpperCase();
  return /^[A-Z][0-9]{6,8}$/.test(clean);
};

/**
 * Valida documentos de tipo "Otro"
 * Longitud: 3-20 caracteres
 */
export const validateOtro = (numDoc) => {
  if (!numDoc) return false;
  const cleanNum = numDoc.replace(/-/g, '');
  return cleanNum.length >= 3 && cleanNum.length <= 20;
};

/**
 * Valida correo electrónico
 */
export const validateEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida número de teléfono de El Salvador
 * Formato: ####-#### (8 dígitos con guión)
 */
export const validatePhone = (phone) => {
  if (!phone) return false;
  // Acepta formatos: ####-####, ########, +503########
  const clean = phone.replace(/\s|-/g, '');
  return /^\d{8}$/.test(clean) || /^\+503\d{8}$/.test(clean);
};

/**
 * Valida número de WhatsApp
 */
export const validateWhatsApp = (whatsapp) => {
  if (!whatsapp) return true; // WhatsApp es opcional
  return whatsapp.length >= 8;
};

/**
 * Valida código de establecimiento (sucursal)
 * Formato: 4 dígitos ( CHAR(4) en base de datos)
 */
export const validateCodigoEstablecimiento = (codigo) => {
  if (!codigo) return false;
  return /^\d{4}$/.test(codigo);
};

/**
 * Valida código de punto de venta
 * Formato: 4 dígitos ( CHAR(4) en base de datos)
 */
export const validateCodigoPuntoVenta = (codigo) => {
  if (!codigo) return false;
  return /^\d{4}$/.test(codigo);
};

/**
 * Valida código de departamento (El Salvador tiene 14 departamentos)
 * Formato: 2 dígitos
 */
export const validateCodigoDepartamento = (codigo) => {
  if (!codigo) return false;
  return /^\d{2}$/.test(codigo);
};

/**
 * Valida código de municipio
 * Formato: 4 caracteres (CHAR(4) en base de datos)
 */
export const validateCodigoMunicipio = (codigo) => {
  if (!codigo) return false;
  return /^[\dA-Z]{4}$/.test(codigo);
};

// ============================================
// MENSAJES DE ERROR
// ============================================

export const ValidationMessages = {
  NIT: 'NIT inválido. Formato: ####-######-###-#',
  DUI: 'DUI inválido. Formato: ########-#',
  NRC: 'NRC inválido. Solo números, máximo 8 dígitos',
  CARNET_RESIDENTE: 'Carnet Residente inválido. Formato: Letra seguida de 6-7 dígitos (ej: A1234567)',
  PASAPORTE: 'Pasaporte inválido. Formato: Letra seguida de 6-8 dígitos (ej: A1234567)',
  OTRO: 'Documento debe tener entre 3 y 20 caracteres',
  EMAIL: 'Correo electrónico inválido',
  PHONE: 'Teléfono inválido. Formato: ####-####',
  WHATSAPP: 'WhatsApp inválido. Mínimo 8 dígitos',
  CODIGO_ESTABLECIMIENTO: 'Código de establecimiento inválido. Debe ser exactamente 4 dígitos',
  CODIGO_PUNTO_VENTA: 'Código de punto de venta inválido. Debe ser exactamente 4 dígitos',
  CODIGO_DEPARTAMENTO: 'Código de departamento inválido. Debe ser 2 dígitos',
  CODIGO_MUNICIPIO: 'Código de municipio inválido. Debe ser 3 dígitos',
};